import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { tokenStore } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Sparkle, ArrowRight, CheckCircle, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function JoinClass() {
  const { code } = useParams();
  const navigate = useNavigate();
  // We don't have a setUser exported from auth; we'll just refresh by navigating
  // — the AuthProvider re-checks /auth/me on mount, but we control state via login flow.
  const { } = useAuth();
  const [classInfo, setClassInfo] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [creds, setCreds] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .get(`/public/class/${code}`)
      .then(({ data }) => setClassInfo(data))
      .catch(() => setError("This class code doesn't seem to exist. Check with your teacher."));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/class/${code}/join`, { name: name.trim() });
      // Persist the freshly-issued Bearer token so /auth/me works on next page
      // load (the backend also sets a cookie, but we treat Bearer as primary).
      if (data.token) tokenStore.set(data.token);
      setCreds(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't join — please try again");
    } finally {
      setBusy(false);
    }
  };

  const copyAll = async () => {
    if (!creds) return;
    try {
      await navigator.clipboard.writeText(
        `Login: ${creds.username}\nPassword: ${creds.password}`
      );
      setCopied(true);
      toast.success("Credentials copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — please write them down manually");
    }
  };

  const goToDashboard = () => {
    // The /public/class/{code}/join endpoint sets the auth cookie. Hard-reload
    // so AuthProvider re-runs /auth/me and picks up the new session.
    window.location.href = "/";
  };

  if (error && !classInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#FFFDF8]">
        <div className="w-full max-w-md text-center" data-testid="join-not-found">
          <h1 className="font-outfit text-3xl font-black mb-2">Hmm.</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/login" className="text-yellow-700 underline font-semibold">Go to sign-in</Link>
        </div>
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="join-loading">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#FFFDF8]">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-2xl bg-yellow-400 flex items-center justify-center">
            <Sparkle weight="fill" className="text-yellow-950" size={22} />
          </div>
          <div>
            <div className="font-outfit font-black text-lg leading-none">Alpha AI Discovery Club</div>
            <div className="text-xs text-slate-500">{classInfo.class_name}</div>
          </div>
        </div>

        {!creds ? (
          <>
            <h1 className="font-outfit text-4xl sm:text-5xl font-black tracking-tight mb-1" data-testid="join-title">
              Join the club.
            </h1>
            <p className="text-slate-600 mb-1">You're joining</p>
            <p className="font-caveat text-2xl text-yellow-700 mb-6" data-testid="join-classname">
              {classInfo.class_name}
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="join-form">
              <div>
                <label className="text-sm font-medium block mb-1">What's your name?</label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="join-name-input"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="Kwame Mensah"
                  maxLength={60}
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  We'll make a username + password for you. No real email needed.
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="join-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || name.trim().length < 2}
                data-testid="join-submit-btn"
                className="btn-tactile-yellow w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-yellow-400 hover:bg-yellow-500 font-outfit font-bold text-yellow-950 disabled:opacity-60"
              >
                {busy ? "Joining..." : "Join the class"} <ArrowRight weight="bold" size={18} />
              </button>
            </form>

            <div className="mt-6 text-sm text-slate-500 text-center">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-slate-900 underline" data-testid="goto-login-link">
                Sign in
              </Link>
            </div>
          </>
        ) : (
          <div data-testid="join-success">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle weight="fill" className="text-emerald-500" size={28} />
              <h1 className="font-outfit text-3xl font-black tracking-tight">
                You're in, {creds.name.split(" ")[0]}!
              </h1>
            </div>
            <p className="text-slate-600 mb-6">
              Here are your login details. Save them somewhere safe — you'll need them next time.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-4">
              <div className="space-y-2">
                <div>
                  <div className="text-xs font-bold tracking-wider uppercase text-amber-800">Username</div>
                  <div className="font-mono text-lg bg-white px-3 py-2 rounded-lg mt-1 select-all" data-testid="join-result-username">
                    {creds.username}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold tracking-wider uppercase text-amber-800">Password</div>
                  <div className="font-mono text-lg bg-white px-3 py-2 rounded-lg mt-1 select-all" data-testid="join-result-password">
                    {creds.password}
                  </div>
                </div>
              </div>
              <button
                onClick={copyAll}
                data-testid="join-copy-btn"
                className="mt-3 w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-xl bg-white border border-amber-200 text-sm font-semibold hover:bg-amber-100"
              >
                {copied ? <CheckCircle size={16} weight="fill" className="text-emerald-500" /> : <Copy size={16} weight="bold" />}
                {copied ? "Copied" : "Copy both"}
              </button>
            </div>

            <button
              onClick={goToDashboard}
              data-testid="join-continue-btn"
              className="btn-tactile-yellow w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-yellow-400 hover:bg-yellow-500 font-outfit font-bold text-yellow-950"
            >
              Go to my dashboard <ArrowRight weight="bold" size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
